import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { GuidedTourService, TourState, TourStep } from '../../services/guided-tour.service';

export interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ConfettiPiece {
  x: number;
  color: string;
  delay: number;
  duration: number;
  size: number;
  isCircle: boolean;
}

const SPOTLIGHT_PAD = 10;
const TOOLTIP_WIDTH = 360;
const TOOLTIP_HEIGHT_EST = 300;
const VIEWPORT_MARGIN = 16;
const GAP = SPOTLIGHT_PAD + 14;
const NAV_SETTLE_DELAY = 450;
const DEFAULT_ACCENT = '#1f89e6';

@Component({
  selector: 'mindbase-guided-tour',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './guided-tour.component.html',
  styleUrl: './guided-tour.component.scss',
})
export class GuidedTourComponent implements OnInit, OnDestroy {
  protected tourActive = false;
  protected currentStep: TourStep | null = null;
  protected currentStepIndex = 0;
  protected totalSteps = 0;
  protected steps: TourStep[] = [];
  protected spotlightRect: SpotlightRect | null = null;
  protected tooltipTop = 0;
  protected tooltipLeft = 0;
  protected resolvedPos: TourStep['tooltipPosition'] = 'bottom';

  protected svgWidth = window.innerWidth;
  protected svgHeight = window.innerHeight;

  protected confettiActive = false;
  protected confettiParticles: ConfettiPiece[] = [];

  private readonly destroy$ = new Subject<void>();
  private positionTimer: ReturnType<typeof setTimeout> | null = null;
  private confettiTimer: ReturnType<typeof setTimeout> | null = null;

  // CSS custom properties on the host so spotlight + tooltip both inherit the accent
  @HostBinding('style.--gt-accent')
  get hostAccent(): string {
    return this.currentStep?.accentColor ?? DEFAULT_ACCENT;
  }

  @HostBinding('style.--gt-accent-bg')
  get hostAccentBg(): string {
    const c = this.currentStep?.accentColor ?? DEFAULT_ACCENT;
    const r = parseInt(c.slice(1, 3), 16);
    const g = parseInt(c.slice(3, 5), 16);
    const b = parseInt(c.slice(5, 7), 16);
    return `rgba(${r},${g},${b},0.12)`;
  }

  constructor(
    private readonly tourService: GuidedTourService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.tourService.tourState$.pipe(takeUntil(this.destroy$)).subscribe((state: TourState) => {
      this.tourActive = state.active;
      this.steps = state.steps;
      this.totalSteps = state.steps.length;
      this.currentStepIndex = state.currentStepIndex;
      this.currentStep = state.steps[state.currentStepIndex] ?? null;
      this.resolvedPos = this.currentStep?.tooltipPosition ?? 'bottom';

      if (state.active && this.currentStep) {
        this.schedulePosition();
      } else {
        this.spotlightRect = null;
      }
      this.cdr.markForCheck();
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (this.tourActive) {
          this.schedulePosition(NAV_SETTLE_DELAY);
        }
      });
  }

  protected get overlayPath(): string {
    const vw = this.svgWidth;
    const vh = this.svgHeight;
    const outer = `M0,0 L${vw},0 L${vw},${vh} L0,${vh} Z`;
    if (!this.spotlightRect) return outer;

    const { top, left, width, height } = this.spotlightRect;
    const r = 10;
    const x = left;
    const y = top;
    const w = width;
    const h = height;
    const hole =
      `M${x + r},${y} ` +
      `L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} ` +
      `L${x + w},${y + h - r} Q${x + w},${y + h} ${x + w - r},${y + h} ` +
      `L${x + r},${y + h} Q${x},${y + h} ${x},${y + h - r} ` +
      `L${x},${y + r} Q${x},${y} ${x + r},${y} Z`;
    return `${outer} ${hole}`;
  }

  protected get progressPercent(): number {
    if (this.totalSteps === 0) return 0;
    return Math.round(((this.currentStepIndex + 1) / this.totalSteps) * 100);
  }

  protected get isFinishStep(): boolean {
    return this.currentStepIndex === this.totalSteps - 1;
  }

  protected get rankLabel(): string {
    const i = this.currentStepIndex;
    if (i <= 1) return 'Rookie';
    if (i <= 3) return 'Explorer';
    if (i <= 5) return 'Learner';
    return 'Champion';
  }

  protected get rankEmoji(): string {
    const i = this.currentStepIndex;
    if (i <= 1) return '🌱';
    if (i <= 3) return '🔍';
    if (i <= 5) return '📚';
    return '🏆';
  }

  @HostListener('window:resize')
  onResize(): void {
    this.svgWidth = window.innerWidth;
    this.svgHeight = window.innerHeight;
    if (this.tourActive) {
      this.schedulePosition();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(_event: Event): void {
    if (this.tourActive) {
      this.updatePosition();
    }
  }

  protected next(): void {
    if (this.isFinishStep) {
      this.triggerConfetti();
    }
    this.tourService.next();
  }

  protected prev(): void {
    this.tourService.prev();
  }

  protected skip(): void {
    this.tourService.skipTour();
  }

  private triggerConfetti(): void {
    const colors = [
      '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6',
      '#ef4444', '#06b6d4', '#fb923c', '#f0abfc', '#ffffff',
    ];
    this.confettiParticles = Array.from({ length: 64 }, (_, i) => ({
      x: (i * 1.58) % 100,
      color: colors[i % colors.length],
      delay: (i % 26) * 65,
      duration: 1800 + (i % 6) * 200,
      size: 7 + (i % 4) * 2,
      isCircle: i % 3 === 0,
    }));
    this.confettiActive = true;
    if (this.confettiTimer !== null) clearTimeout(this.confettiTimer);
    this.confettiTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.confettiActive = false;
        this.confettiParticles = [];
        this.cdr.detectChanges();
      });
    }, 4200);
  }

  private schedulePosition(delay = 120): void {
    if (this.positionTimer !== null) {
      clearTimeout(this.positionTimer);
    }
    this.positionTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.updatePosition();
        this.cdr.detectChanges();
      });
    }, delay);
  }

  private updatePosition(): void {
    if (!this.currentStep) return;

    const el = document.getElementById(this.currentStep.targetId);
    if (!el) {
      this.schedulePosition(200);
      return;
    }

    el.scrollIntoView({ behavior: 'instant', block: 'center' });

    const rect = el.getBoundingClientRect();
    this.spotlightRect = {
      top: rect.top - SPOTLIGHT_PAD,
      left: rect.left - SPOTLIGHT_PAD,
      width: rect.width + SPOTLIGHT_PAD * 2,
      height: rect.height + SPOTLIGHT_PAD * 2,
    };

    this.positionTooltip(rect);
  }

  private positionTooltip(targetRect: DOMRect): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const preferredPos = this.currentStep?.tooltipPosition ?? 'bottom';
    let top = 0;
    let left = 0;
    let resolvedPos = preferredPos;

    if (preferredPos === 'bottom') {
      top = targetRect.bottom + GAP;
      left = targetRect.left;
      if (top + TOOLTIP_HEIGHT_EST > vh - VIEWPORT_MARGIN) {
        top = targetRect.top - TOOLTIP_HEIGHT_EST - GAP;
        resolvedPos = 'top';
      }
    } else if (preferredPos === 'top') {
      top = targetRect.top - TOOLTIP_HEIGHT_EST - GAP;
      left = targetRect.left;
      if (top < VIEWPORT_MARGIN) {
        top = targetRect.bottom + GAP;
        resolvedPos = 'bottom';
      }
    } else if (preferredPos === 'right') {
      top = targetRect.top;
      left = targetRect.right + GAP;
      if (left + TOOLTIP_WIDTH > vw - VIEWPORT_MARGIN) {
        left = targetRect.left - TOOLTIP_WIDTH - GAP;
        resolvedPos = 'left';
      }
    } else {
      top = targetRect.top;
      left = targetRect.left - TOOLTIP_WIDTH - GAP;
      if (left < VIEWPORT_MARGIN) {
        left = targetRect.right + GAP;
        resolvedPos = 'right';
      }
    }

    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN));
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - TOOLTIP_HEIGHT_EST - VIEWPORT_MARGIN));

    this.tooltipTop = top;
    this.tooltipLeft = left;
    this.resolvedPos = resolvedPos;
  }

  ngOnDestroy(): void {
    if (this.positionTimer !== null) clearTimeout(this.positionTimer);
    if (this.confettiTimer !== null) clearTimeout(this.confettiTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
